import React, { createContext, useEffect, useMemo, useState } from 'react';

export const LanguageContext = createContext();

const LANGUAGE_CODES = ['en', 'es', 'ca'];
const LANGUAGE_STORAGE_KEY = 'vatti-language';

const getInitialLanguage = () => {
  if (typeof window === 'undefined') {
    return 'en';
  }

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return LANGUAGE_CODES.includes(storedLanguage) ? storedLanguage : 'en';
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(getInitialLanguage);
  const [translationsCache, setTranslationsCache] = useState({});
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isActive = true;

    const loadAllTranslations = async () => {
      const results = await Promise.allSettled(
        LANGUAGE_CODES.map(async (code) => {
          const response = await fetch(`/translations/${code}.json`);

          if (!response.ok) {
            throw new Error(`Unable to load ${code} translations (${response.status})`);
          }

          const data = await response.json();
          return [code, data];
        })
      );

      if (!isActive) {
        return;
      }

      const nextCache = {};

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const [code, data] = result.value;
          nextCache[code] = data;
          return;
        }

        console.error('Error loading translations:', result.reason);
      });

      setTranslationsCache(nextCache);
      setIsReady(true);
    };

    loadAllTranslations();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    }

    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const translations = useMemo(
    () => translationsCache[lang] || translationsCache.en || {},
    [lang, translationsCache]
  );

  const t = (key) => translations[key] || key;

  if (!isReady && !Object.keys(translationsCache).length) {
    return <div>Loading translations...</div>;
  }

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
